/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, increment, serverTimestamp, setDoc } from "firebase/firestore";
import { Mail, LockKeyhole, Eye, EyeOff, Fingerprint } from "lucide-react";

import { auth, db } from "../firebase";
import logoWhite from "../assets/presentplus-logo-white.png";
import parentLoginBg from "../assets/parent-login-bg.png";
import "./ParentLogin.css";

import {
  BIOMETRIC_UNLOCK_SESSION_KEY,
  getBiometricAvailability,
  getStoredCredentials,
  verifyBiometric,
} from "../utils/biometricAuth";

const REMEMBERED_EMAIL_KEY = "pp_parent_remembered_email";

async function recordParentLogin(user) {
  if (!user?.uid) return;

  try {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    const existing = userSnap.exists() ? userSnap.data() || {} : {};

    const next = {
      lastLoginAt: serverTimestamp(),
      lastActiveAt: serverTimestamp(),
      loginCount: increment(1),
      onboardingStatus: "first_login",
      engagementStatus: "active",
      activityUpdatedAt: serverTimestamp(),
    };

    if (!existing.firstLoginAt) {
      next.firstLoginAt = serverTimestamp();
    }

    if (!existing.accountCreatedAt) {
      next.accountCreatedAt = serverTimestamp();
    }

    await setDoc(userRef, next, { merge: true });
  } catch (error) {
    console.warn("Parent login activity could not be recorded:", error);
  }
}


function normaliseEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export default function ParentLogin() {
  const navigate = useNavigate();

  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  function keepInputVisible(ref) {
    window.setTimeout(() => {
      ref?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }, 220);
  }

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(true);

  const [biometricReady, setBiometricReady] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState("Quick unlock");
  const [biometricBusy, setBiometricBusy] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const busy = loading || biometricBusy;
  const canUseBiometricButton = biometricReady && !busy;

  useEffect(() => {
    let alive = true;

    async function bootstrapLogin() {
      try {
        const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";
        if (alive && savedEmail) setEmail(savedEmail);

        const credentials = await getStoredCredentials();

        if (!alive) return;

        if (credentials?.username && !savedEmail) {
          setEmail(credentials.username);
        }

        if (credentials?.username && credentials?.password) {
          setBiometricReady(true);

          // Availability can be slower than reading secure credentials, so update
          // the label after the button is already visible.
          getBiometricAvailability()
            .then((availability) => {
              if (!alive) return;
              setBiometricReady(Boolean(availability?.isAvailable));
              setBiometricLabel(availability?.label || "Face ID");
            })
            .catch(() => {
              if (alive) setBiometricLabel("Face ID");
            });
        } else {
          setBiometricReady(false);
        }
      } catch (err) {
        console.warn("Biometric login check skipped:", err);
        if (alive) setBiometricReady(false);
      }
    }

    bootstrapLogin();

    return () => {
      alive = false;
    };
  }, []);

  async function handleBiometricLogin() {
    if (!canUseBiometricButton) return;

    setError("");
    setBiometricBusy(true);

    try {
      const credentials = await getStoredCredentials();

      if (!credentials?.username || !credentials?.password) {
        setBiometricReady(false);
        setError("Face ID is not ready yet. Sign in with your password and enable it again from Profile.");
        return;
      }

      await verifyBiometric({
        reason: "Unlock PresentPlus",
        title: "PresentPlus",
        subtitle: "Use your device security",
        description: "Confirm it is you to open your family dashboard.",
        negativeButtonText: "Use password",
      });

      sessionStorage.setItem(
        BIOMETRIC_UNLOCK_SESSION_KEY,
        String(Date.now())
      );


      const cleanEmail = normaliseEmail(credentials.username);

      const result = await signInWithEmailAndPassword(
        auth,
        cleanEmail,
        credentials.password
      );

      localStorage.setItem(REMEMBERED_EMAIL_KEY, cleanEmail);

      console.log("PARENT BIOMETRIC LOGIN SUCCESS", result.user?.uid);

      await recordParentLogin(result.user);
      navigate("/parent/home", { replace: true });
    } catch (err) {
      console.warn("Biometric login skipped:", err);
      setError("Quick unlock was cancelled or could not be completed. Please use your password.");
    } finally {
      setBiometricBusy(false);
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    if (busy) return;

    setError("");

    const cleanEmail = normaliseEmail(email);

    if (!cleanEmail || !password) {
      setError("Please enter your email and password.");
      return;
    }

    try {
      setLoading(true);

      console.log("PARENT LOGIN START", cleanEmail);

      const result = await signInWithEmailAndPassword(
        auth,
        cleanEmail,
        password
      );

      console.log("PARENT FIREBASE LOGIN SUCCESS", result.user?.uid);

      await recordParentLogin(result.user);

      if (rememberEmail) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, cleanEmail);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      navigate("/parent/home", { replace: true });
    } catch (err) {
      console.error("Parent login failed:", err);

      const code = String(err?.code || "");

      if (
        code.includes("invalid-credential") ||
        code.includes("wrong-password") ||
        code.includes("user-not-found")
      ) {
        setError("Invalid email or password. Please use the same email and password created from the school invite.");
      } else if (code.includes("too-many-requests")) {
        setError("Too many attempts. Please wait a moment before trying again.");
      } else if (code.includes("network")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(err?.message || "We could not sign you in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  const quickUnlockText = useMemo(() => {
    if (biometricBusy) return "Checking…";
    return `Continue with ${biometricLabel || "Face ID"}`;
  }, [biometricBusy, biometricLabel]);

  const blurInputWhenTappingOutside = (event) => {
    const target = event.target;

    if (
      target?.closest?.("input") ||
      target?.closest?.("textarea") ||
      target?.closest?.("button") ||
      target?.closest?.("a")
    ) {
      return;
    }

    if (
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }
  };

  return (
    <main
      className="pp-login-page"
      style={{ "--pp-login-bg": `url(${parentLoginBg})` }}
    >
      <section
        className="pp-login-card"
        aria-labelledby="pp-parent-login-title"
        aria-busy={busy}
        onPointerDownCapture={blurInputWhenTappingOutside}
      >
        <div className="pp-login-top">
          <img src={logoWhite} alt="PresentPlus" className="pp-login-logo" />

          <h1 id="pp-parent-login-title">Welcome back</h1>
          <p className="pp-login-copy">
            Sign in securely to continue to your family dashboard.
          </p>
        </div>

        <div className="pp-login-form-panel">
        {biometricReady ? (
          <button
            type="button"
            className="pp-login-biometric"
            onClick={handleBiometricLogin}
            disabled={!canUseBiometricButton}
            aria-label={`Continue with ${biometricLabel || "biometric unlock"}`}
          >
            <span className="pp-login-biometric-icon">
              <Fingerprint size={20} aria-hidden="true" />
            </span>
            <span>{quickUnlockText}</span>
          </button>
        ) : null}

        <form className="pp-login-form" onSubmit={handleLogin} noValidate>
          <label className="pp-login-field">
            <span>School-registered email</span>
            <div className="pp-login-input-wrap">
              <Mail size={18} aria-hidden="true" />
              <input
                className="pp-login-input"
                type="email"
                autoComplete="email"
                placeholder="parent@example.com"
                ref={emailInputRef}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => keepInputVisible(emailInputRef)}
                disabled={busy}
                required
                aria-required="true"
                aria-describedby={error ? "pp-parent-login-error" : undefined}
              />
            </div>
          </label>

          <label className="pp-login-field">
            <span>Password</span>
            <div className="pp-login-input-wrap">
              <LockKeyhole size={18} aria-hidden="true" />
              <input
                className="pp-login-input"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                ref={passwordInputRef}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => keepInputVisible(passwordInputRef)}
                disabled={busy}
                required
                aria-required="true"
                aria-describedby={error ? "pp-parent-login-error" : undefined}
              />

              <button
                type="button"
                className="pp-login-eye"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={busy}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>

          <div className="pp-login-row">
            <button
              type="button"
              className="pp-login-remember"
              onClick={() => setRememberEmail((current) => !current)}
              aria-pressed={rememberEmail}
              disabled={busy}
            >
              <span className={rememberEmail ? "active" : ""}>
                {rememberEmail ? "✓" : ""}
              </span>
              Remember email
            </button>

            <Link to="/parent-forgot-password" className="pp-login-forgot">
              Forgot?
            </Link>
          </div>

          {error ? (
            <div id="pp-parent-login-error" className="pp-login-error" role="alert" aria-live="assertive">
              {error}
            </div>
          ) : null}

          <button type="submit" className="pp-login-submit" disabled={busy} aria-disabled={busy}>
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        </div>
      </section>
    </main>
  );
}
